/**
 * Templates - Singleton for Handlebars template system and custom helpers
 *
 * SINGLETON: Manages Handlebars template compilation and provides 24 custom helpers
 * for common template operations (number formatting, date formatting, conditionals,
 * collection manipulation, i18n integration).
 *
 * PUBLIC API NOTE: All public methods auto-inject caller context as first argument.
 * JSDoc @param lists show only user-provided parameters (what you actually pass).
 *
 * CUSTOM HELPERS (24 total):
 * Logic: compare, contains, missing, isNull, isInt, isFinite
 * Collections: chunk, slice, join, concat
 * Numbers: printNumber, printCurrencySymbol, sum, product, sIfPlural, formatNumber
 * Dates: formatDate, formatLocaleDate, dateTimeLocale, dateTimeRangeLocale, locationLocale
 * Utilities: encodeURI, evenOdd, getIcon
 *
 * ICON MODES:
 * - Font icons (icon fonts)
 * - SVG icons (not yet implemented)
 *
 * I18N INTEGRATION:
 * Custom helpers for locale-aware date/time rendering that work with LocalizeJS
 * for client-side localization without re-rendering.
 *
 * @class Templates
 * @extends Class
 * @singleton
 *
 * @example
 * // Access via dependency
 * requires: ['Templates']
 *
 * @example
 * // Set icon mode
 * Templates.setIconMode('font');
 *
 * @example
 * // Use custom helper in template
 * {{printNumber price 'currency' 'USD'}}
 * {{formatDate '%B %d, %Y' timestamp timezone}}
 * {{#compare value 5 '>'}}Greater than 5{{/compare}}
 *
 * @see Handlebars documentation for built-in helpers
 */

define("Templates", [`Class`, `module`, `handlebars`, `numeral`], function (Super, Self, Handlebars, numeral) {
	function insertCommas(numberString) {
		var segments = numberString.split(`.`);
		segments[0] = segments[0].replace(/\B(?=(\d{3})+(?!\d))/g, `,`);
		return segments.join(`.`);
	}

	// ISO 4217 minor unit exceptions; any currency not listed defaults to 2 decimal places
	const DEFAULT_CURRENCY_DECIMAL_PLACES = 2;
	const CURRENCY_DECIMAL_PLACES = {
		JPY: 0,
		KRW: 0,
	};

	function getCurrencyDecimalPlaces(currency) {
		const currencyOverride = CURRENCY_DECIMAL_PLACES[currency];
		const hasOverride = Number.isInteger(currencyOverride, 0);

		return hasOverride ? currencyOverride : DEFAULT_CURRENCY_DECIMAL_PLACES;
	}

	let iconMode;
	let DATE_TIME_FORMATS;

	return Super.inherit({
		namespace: Self.id,
		requires: [`DOM`, `Time`, `XHR`],
		singleton: true,
		prepare(setup) {
			const _ = this.require(`lodash`);
			const _this = this;
			let helpers;

			DATE_TIME_FORMATS = this.Time.getDateTimeFormats();

			_.templateSettings = {
				// set up simple mustache-template rendering, used for this.mustache() below
				interpolate: /\{\{(.+?)\}\}/g,
			};

			helpers = {
				concat(v1, v2, v3, v4, v5, v6, v7, v8, v9, options) {
					const args = _this.initial(arguments);
					const result = args.join(``);
					return result;
					// return new Handlebars.SafeString(result);
				},
				compare(val1, val2, operator, options) {
					if (_this.isUndefined(options)) {
						// only three arguments, meaning two passed by the template
						options = operator; // options is the third argument
						operator = `===`; // operator wasn't provided, use equality
					}

					var result;
					switch (
						operator // disable jshint warning for using ==, !=
					) {
						/*jshint -W116 */ case `===`:
							result = val1 === val2;
							break;
						case `==`:
							result = val1 == val2;
							break;
						case `!=`:
							result = val1 != val2;
							break;
						case `>`:
							result = val1 > val2;
							break;
						case `<`:
							result = val1 < val2;
							break;
						case `>=`:
							result = val1 >= val2;
							break;
						case `<=`:
							result = val1 <= val2;
							break;
						/*jshint +W116 */
					}
					return result ? options.fn(this) : options.inverse(this);
				},
				contains(haystack, needle, options) {
					const result = _this.includes(haystack, needle);
					return result ? options.fn(this) : options.inverse(this);
				},
				missing(haystack, needle, options) {
					const result = !_this.includes(haystack, needle);
					return result ? options.fn(this) : options.inverse(this);
				},

				chunk(values, size, options) {
					// break a collection into a set of subcollections, and iterate
					var result;

					if (_this.isUndefined(options) || !_this.isInt(size, 1)) {
						throw new Error(`Invalid size parameter passed`);
					}

					_this
						.chain(values)
						.chunk(size)
						.reduce(function (memo, item, i) {
							return memo + options.fn(item);
						}, ``) // start with an empty string
						.tap(function (markup) {
							result = markup;
						});

					return new Handlebars.SafeString(result);
				},

				slice(values, begin, end, options) {
					// filter a collection or string down to a sub-list
					var result;

					if (!_this.isInt(begin)) {
						throw new Error(`Invalid begin parameter passed`);
					}

					if (_this.isUndefined(options)) {
						options = end;
						end = undefined; // reset to pass into slice() below
					}

					if (_this.isString(values)) {
						// slicing a string
						result = values.slice(begin, end);
						return new Handlebars.SafeString(result);
					} else if (!_this.isArray(values)) {
						// not a usable list, swallow error in case a 'null' was passed instead of [ ], for example
						return new Handlebars.SafeString(``); // don't render anything
					}

					_this
						.chain(values)
						.slice(begin, end)
						.reduce(function (memo, item, i) {
							return memo + options.fn(item);
						}, ``) // start with an empty string
						.tap(function (markup) {
							result = markup;
						});

					return new Handlebars.SafeString(result);
				},

				isNull(val, options) {
					return _this.isNull(val) ? options.fn(this) : options.inverse(this);
				},

				isInt(val, options) {
					return _this.isInt(val) ? options.fn(this) : options.inverse(this);
				},

				isFinite(val, options) {
					return _this.isFinite(val) ? options.fn(this) : options.inverse(this);
				},

				join() {
					var values = _this.reduce(
							_this.initial(arguments),
							function (memo, arg) {
								if (_this.isString(arg, 1) || _this.isFinite(arg)) {
									memo.push(arg);
								} else if (_this.isArray(arg, 1)) {
									arg.forEach((a) => {
										if (_this.isString(a, 1) || _this.isFinite(arg)) {
											memo.push(a);
										}
									});
								}

								return memo;
							},
							[]
						),
						deliminator;

					if (values.length < 2) {
						return ``;
					} else {
						deliminator = values.pop();
						return new Handlebars.SafeString(values.join(deliminator));
					}
				},

				sum() {
					var values = _this.initial(arguments);
					return new Handlebars.SafeString(
						_this.reduce(
							values,
							function (memo, num) {
								return memo + num;
							},
							0
						)
					);
				},

				product() {
					var values = _this.initial(arguments);
					return new Handlebars.SafeString(
						_this.reduce(
							values,
							function (memo, num) {
								return memo * num;
							},
							1
						)
					);
				},

				sIfPlural(val) {
					return new Handlebars.SafeString(val === 1 ? `` : `s`);
				},

				/**
				 * Return the currency symbol for the given currency code
				 */
				printCurrencySymbol(currency) {
					try {
						const { locale } = _this.data;

						return new Intl.NumberFormat(locale, { style: `currency`, currency })
							.formatToParts(1)
							.find((x) => x.type === `currency`).value;
					} catch (e) {
						return null;
					}
				},

				printNumber(value, type, arg1, customClass) {
					var absoluteValue = Math.abs(value),
						numberString = ``,
						spacer = `\u00A0`;

					if (_.isNull(value) || _.isUndefined(value)) {
						// not passed a real value
						return new Handlebars.SafeString(``);
					}

					switch (type) {
						case `currency`:
						case `currencyCentsOptional`:
						case `currencyCompact`: {
							const decimalPlaces = getCurrencyDecimalPlaces(arg1);
							const alwaysShowsDecimals = type === `currency`;
							const hasFractionalValue = value !== parseInt(value, 10);
							const showDecimals = decimalPlaces > 0 && (alwaysShowsDecimals || hasFractionalValue);

							numberString += showDecimals ? absoluteValue.toFixed(decimalPlaces) : Math.round(absoluteValue);
							numberString = insertCommas(numberString);

							if (value < 0) {
								numberString = `(` + numberString + `)`;
							}

							if (type === `currencyCompact`) {
								spacer = ``;
							}

							if (_this.isString(arg1)) {
								switch (
									arg1 // print the currency symbol, if an extra param was passed
								) {
									case `CAD`:
										numberString = `\u0024${spacer}${numberString}${spacer}CAD`;
										break;
									case `EUR`:
										numberString = `\u20AC${spacer}${numberString}`;
										break;
									case `GBP`:
										numberString = `\u00A3${spacer}${numberString}`;
										break;
									case `NZD`:
										numberString = `\u0024${spacer}${numberString}${spacer}NZD`;
										break;
									case `USD`:
										numberString = `\u0024${spacer}${numberString}`;
										break;
									case `KRW`:
										numberString = `\u20A9${spacer}${numberString}`;
										break;
									case `NGN`:
										numberString = `\u20A6${spacer}${numberString}`;
										break;

									default:
										if (_this.isString(customClass)) {
											numberString = `${numberString}<span class="${customClass}">\u00A0${arg1}</span>`;
										} else {
											numberString = `${numberString}\u00A0${arg1}`;
										}
										break;
								}
							}
							break;
						}
						case `percentage`:
							numberString += value.toFixed(1) + `%`;
							break;
						case `abbreviated`:
							if (absoluteValue < 1000) {
								numberString += value;
							} else if (absoluteValue < 1000000) {
								numberString += (value / 1000).toFixed(1) + `K`;
							} else if (absoluteValue < 1000000000) {
								numberString += (value / 1000000).toFixed(1) + `M`;
							} else {
								numberString += (value / 1000000000).toFixed(1) + `B`;
							}
							break;
						case `abbreviatedCurrency`:
						case `abbreviatedCompactCurrency`: {
							const decimalPlaces = getCurrencyDecimalPlaces(arg1);
							const hasFractionalValue = decimalPlaces > 0 && value !== this.parseInt(value);

							if (absoluteValue < 1000) {
								numberString += hasFractionalValue ? absoluteValue.toFixed(decimalPlaces) : Math.round(absoluteValue);
							} else if (absoluteValue < 1000000) {
								numberString += (absoluteValue / 1000).toFixed(1) + `K`;
							} else if (absoluteValue < 1000000000) {
								numberString += (absoluteValue / 1000000).toFixed(1) + `M`;
							} else {
								numberString += (absoluteValue / 1000000000).toFixed(1) + `B`;
							}

							if (value < 0) {
								numberString = `(` + numberString + `)`;
							}

							if (type === `abbreviatedCompactCurrency`) {
								spacer = ``;
							}

							if (_this.isString(arg1)) {
								switch (
									arg1 // print the currency symbol, if an extra param was passed
								) {
									case `USD`:
										numberString = `$${spacer}${numberString}`;
										break;
									case `GBP`:
										numberString = `\u00A3${spacer}${numberString}`;
										break;
									case `EUR`:
										numberString = `\u20AC${spacer}${numberString}`;
										break;
									case `KRW`:
										numberString = `\u20A9${spacer}${numberString}`;
										break;
									default:
										if (_this.isString(customClass)) {
											numberString = `${numberString}<span class="${customClass}">${spacer}${arg1}</span>`;
										} else {
											numberString = `${numberString}${spacer}${arg1}`;
										}
										break;
								}
							}
							break;
						}
						default:
							numberString += value;
							numberString = insertCommas(numberString);
					}

					return new Handlebars.SafeString(numberString);
				},

				encodeURI(content) {
					var result = _this.isString(content) ? encodeURI(content) : ``;

					return new Handlebars.SafeString(result);
				},

				evenOdd: function (value, even, odd) {
					if (value % 2 == 0) {
						return new Handlebars.SafeString(odd);
					} else {
						return new Handlebars.SafeString(even);
					}
				},

				getIcon(name, extraClass, numberOfPaths) {
					if (!_this.isString(name)) {
						name = ``;
					}

					return new Handlebars.SafeString(_this.getIcon(name.toLowerCase(), extraClass, numberOfPaths));
				},

				formatDate(template, timestamp, timezone) {
					var dateString = _this.Time.format(template, timestamp, timezone);
					return new Handlebars.SafeString(dateString);
				},

				formatLocaleDate: function (timestamp, mode) {
					const formatter =
						mode === `verbose`
							? new Intl.DateTimeFormat(`default`, { day: `numeric`, month: `short`, year: `numeric` })
							: new Intl.DateTimeFormat();
					return formatter.format(timestamp);
				},

				formatNumber(number, format) {
					return numeral(number).format(format);
				},

				/**
				 * Note on the variable name in LocalizeJS.
				 * If a plain attribute (with no =) is set in extraAttributes, it will be used as the variable name in LocalizeJS because LocalizeJS will use the first plain attribute it sees.
				 * You an use this override the default var name of dt_date_time.
				 */
				dateTimeLocale(format, timestamp, timezone, locale, extraAttributes) {
					const dateTimeString = _this.getDateTimeLocale({ format, timestamp, timezone, locale, extraAttributes });

					return new Handlebars.SafeString(dateTimeString);
				},

				/**
				 * Note on the variable name in LocalizeJS.
				 * If a plain attribute (with no =) is set in extraAttributes, it will be used as the variable name in LocalizeJS because LocalizeJS will use the first plain attribute it sees.
				 * You an use this override the default var name of dt_date_time.
				 */
				dateTimeRangeLocale(format, startTimestamp, endTimestamp, timezone, locale, extraAttributes) {
					const dateTimeRangeString = _this.getDateTimeRangeLocale({
						format,
						startTimestamp,
						endTimestamp,
						timezone,
						locale,
						extraAttributes,
					});

					return new Handlebars.SafeString(dateTimeRangeString);
				},

				locationLocale(venue, format, prefix) {
					const locationString = _this.getLocationLocale({
						venue,
						format,
						prefix,
					});

					return new Handlebars.SafeString(locationString);
				},
			};

			this.each(helpers, (helper, name) => Handlebars.registerHelper(name, helper));
		},

		api: {
			/**
			 * Set icon rendering mode.
			 *
			 * @method Templates#setIconMode
			 * @param {string} mode - Icon mode: 'font' or 'svg' (svg not yet implemented)
			 */
			setIconMode: {
				public: true,
				value(context, mode) {
					if (mode === `font` || mode === `svg`) {
						iconMode = mode;
					} else {
						throw new Error(`Unrecognized iconMode value "${mode}", expecting "font" or "svg"`);
					}
				},
			},

			/**
			 * Get compiled Handlebars template function.
			 *
			 * @method Templates#get
			 * @param {Object} [options] - Template options
			 * @param {string} [options.namespace] - Class namespace (defaults to caller's)
			 * @param {string} [options.name='main'] - Template name
			 * @returns {Function} Compiled template function
			 */
			get: {
				public: true,
				defaults: {
					name: `main`,
				},
				value(context, options) {
					this.defaults(options, {
						namespace: context.namespace,
					});

					var templates = this.require(options.namespace + `.templates`);

					return templates[options.namespace + `/` + options.name];
				},
			},

			/**
			 * Get registered Handlebars helper function.
			 *
			 * @method Templates.getHelper
			 * @static
			 * @param {string} name - Helper name
			 * @returns {Function} Helper function
			 */
			getHelper: {
				static: true,
				public: true,
				value(name) {
					return Handlebars.helpers[name];
				},
			},

			/**
			 * Generate icon markup.
			 *
			 * @method Templates#getIcon
			 * @param {string} name - Icon name
			 * @param {string} [customClass] - Additional CSS class
			 * @param {number} [numberOfPaths=2] - Number of paths for multi-color font icons
			 * @returns {string} Icon HTML markup
			 */
			getIcon: {
				public: true,
				value(context, name, customClass, numberOfPaths) {
					const className = this.isString(customClass) ? `icon ` + customClass : `icon`;

					if (iconMode === `font`) {
						if (this.isString(customClass, 1) && customClass.indexOf(`colored`) > -1) {
							let paths = ``;

							if (typeof numberOfPaths !== `number`) {
								numberOfPaths = 2; // default for backwards compatibility
							}

							for (let i = 1; i <= numberOfPaths; i++) {
								paths += `<span class="path${i}"></span>`;
							}

							return `<span class="${className} icon-${name}" aria-hidden="true">${paths}</span>`;
						} else {
							return `<span class="${className} icon-${name}" aria-hidden="true"></span>`;
						}
					} else if (iconMode === `svg`) {
						throw new Error(`SVG icons not yet implemented.`);
					} else {
						throw new Error(`No iconMode set`);
					}
				},
			},

			/**
			 * Create icon as DOM element.
			 *
			 * @method Templates#getIconElement
			 * @param {string} name - Icon name
			 * @returns {Element} Icon element
			 */
			getIconElement: {
				public: true,
				value(context, name) {
					var element = document.createElement(`div`);
					element.innerHTML = this.getIcon(name);
					return element.firstChild;
				},
			},

			/**
			 * @typedef {Object} DateTimeLocaleOptions
			 * @note A note on varName and extraAttributes. If a plain attribute (with no =) is set in extraAttributes, it will be used as the variable name in LocalizeJS because LocalizeJS will use the first plain attribute it sees.
			 * @property {string} format - Format the coincides with Time.DATE_TIME_FORMATS. Can also pass in a template string which is parsed into a format.
			 * @property {integer} timestamp - Required: Unix timestamp to format
			 * @property {string} [timezone] - Optional: Timezone to format the timestamp in. If not provided, Time.formatLocale will determine the timezone.
			 * @property {string} [locale]  - Optional: Locale to format the timestamp in. If not provided, DateTimeLocaleObserver will determine the locale.
			 * @property {string} [extraAttributes] - Optional: Extra attributes to add to the span element. Defaults to dt_date_time.
			 * @property {string} [varName] - Optional: The name of the LocalizeJS variable to use in the markup.
			 */
			/**
			 * Generate locale-aware date/time markup for LocalizeJS.
			 *
			 * @method Templates#getDateTimeLocale
			 * @param {Object} [options] - Date/time options
			 * @param {string} options.format - Format name or template
			 * @param {number} options.timestamp - Unix timestamp
			 * @param {string} [options.timezone] - Timezone name
			 * @param {string} [options.locale] - Locale code
			 * @param {string} [options.extraAttributes] - Additional HTML attributes
			 * @param {string} [options.varName='dt_date_time'] - LocalizeJS variable name
			 * @returns {string} HTML markup with locale data attributes
			 */
			getDateTimeLocale: {
				public: true,
				value(_, options = {}) {
					const { format } = options;

					// _FULL formats needs special handling to localize properly
					if (format.includes(`_FULL`)) {
						return this.getDateTimeFullLocale(options); // forward to getDateTimeFullLocale
					}

					const { timestamp, timezone, locale, extraAttributes, varName = `dt_date_time` } = options;
					const attributes = []; // adds dt_ prefix to make it easier to find in LocalizeJS

					if (this.isInt(timestamp)) {
						attributes.push(`data-locale-time='${timestamp}'`);
					}

					if (this.isString(format)) {
						attributes.push(`data-format='${format}'`);
					}

					if (this.isString(timezone)) {
						attributes.push(`data-timezone='${timezone}'`);
					}

					if (this.isString(locale)) {
						attributes.push(`data-locale='${locale}'`);
					}

					if (this.isString(extraAttributes)) {
						attributes.push(extraAttributes);
					}

					if (this.isString(varName)) {
						attributes.push(varName); // add the varName last. Users can pass in a var name via extraAttributes if they want override the var name
					}

					return `<var ${attributes.join(` `)}></var>`;
				},
			},

			/**
			 * Helper method for getDateTimeLocale to generate a full date and time string
			 * @method Templates#getDateTimeFullLocale
			 * @param {DateTimeLocaleOptions} options
			 * @param {string} options.format - Format must be one of the _FULL formats in Time.DATE_TIME_FORMATS and the date prefix must be in DATE_TIME_FORMATS.
			 * @returns {string} - The generated markup for the DateTimeLocaleObserver.
			 */
			getDateTimeFullLocale: {
				private: true,
				value(options = {}) {
					const { format } = options;
					const dateFormat = format.substring(0, format.indexOf(`_TIME`));
					const timeFormat = format.substring(format.indexOf(`_TIME`) + 1, format.indexOf(`_FULL`));

					if (!DATE_TIME_FORMATS[dateFormat] || !DATE_TIME_FORMATS[timeFormat]) {
						this.log({
							type: `error`,
							message: `Invalid full format: %s. Format's date and time portions must be in DATE_TIME_FORMATS.`,
							args: [format],
						});

						throw new Error(`Invalid format: ${format}`);
					}

					const dateOptions = this.assign({}, options, { format: dateFormat, varName: `dt_date` });
					const date = this.getDateTimeLocale(dateOptions);
					const timeOptions = this.assign({}, options, { format: timeFormat, varName: `dt_time` });
					const time = this.getDateTimeLocale(timeOptions);

					return `${date} at ${time}`; // exit early
				},
			},

			/**
			 * @typedef {Object} DateTimeRangeLocaleOptions
			 * @property {string} format - Format the coincides with Time.DATE_TIME_FORMATS. Can also pass in a template string which is parsed into a format.
			 * @property {integer} startTimestamp - Required: Unix timestamp to format
			 * @property {integer} endTimestamp - Required: Unix timestamp to format
			 * @property {string} [timezone] - Optional: Timezone to format the timestamp in. If not provided, Time.formatLocale will determine the timezone.
			 * @property {string} [locale]  - Optional: Locale to format the timestamp in. If not provided, DateTimeLocaleObserver will determine the locale.
			 * @property {string} [extraAttributes] - Optional: Extra attributes to add to the span element.
			 * @property {string} [varName] - Optional: The name of the LocalizeJS variable to use in the markup. Defaults to dt_date_time.
			 */
			/**
			 * Generate locale-aware date/time range markup for LocalizeJS.
			 *
			 * @method Templates#getDateTimeRangeLocale
			 * @param {Object} [options] - Date/time range options
			 * @param {string} options.format - Format name or template
			 * @param {number} options.startTimestamp - Start timestamp
			 * @param {number} options.endTimestamp - End timestamp
			 * @param {string} [options.timezone] - Timezone name
			 * @param {string} [options.locale] - Locale code
			 * @param {string} [options.extraAttributes] - Additional HTML attributes
			 * @param {string} [options.varName='dt_date_time'] - LocalizeJS variable name
			 * @returns {string} HTML markup with locale data attributes
			 */
			getDateTimeRangeLocale: {
				public: true,
				value(_, options = {}) {
					const {
						format,
						startTimestamp,
						endTimestamp,
						timezone,
						locale,
						extraAttributes,
						varName = `dt_date_time`,
					} = options;
					const attributes = [];

					if (this.isInt(startTimestamp)) {
						attributes.push(`data-locale-time='${startTimestamp}'`);
					}

					if (this.isInt(endTimestamp)) {
						attributes.push(`data-locale-time-end='${endTimestamp}'`);
					}

					if (this.isString(format)) {
						attributes.push(`data-format='${format}'`);
					}

					if (this.isString(timezone)) {
						attributes.push(`data-timezone='${timezone}'`);
					}

					if (this.isString(locale)) {
						attributes.push(`data-locale='${locale}'`);
					}

					if (this.isString(extraAttributes)) {
						attributes.push(extraAttributes);
					}

					attributes.push(varName); // add the varName last. Users can pass in a var name via extraAttributes if they want override the var name

					return `<var ${attributes.join(` `)}></var>`;
				},
			},

			/**
			 * @typedef {Object} LocationLocaleOptions
			 * @property {Object} venue - venue location information.
			 * @property {string} format - the location format. (NAME, CITY_STATE, NAME_CITY_STATE)
			 * @property {string} prefix - the prefix to add to the location. Used to connect text before the location Like " at " or " in "
			 */
			/**
			 * Generate locale-aware location markup for LocalizeJS.
			 *
			 * @method Templates#getLocationLocale
			 * @param {Object} [options] - Location options
			 * @param {Object} [options.venue] - Venue object with name, city, state
			 * @param {string} [options.format='NAME_CITY_STATE'] - Format pattern
			 * @param {string} [options.prefix=''] - Prefix text (e.g., ' at ')
			 * @returns {string} HTML markup with locale variable
			 */
			getLocationLocale: {
				public: true,
				value(_, options = {}) {
					this.defaults(options, {
						venue: {},
						format: `NAME_CITY_STATE`,
						prefix: ``,
					});

					const { venue, format, prefix } = options;
					const { name, city, state } = venue;
					const includesName = [`NAME`, `NAME_CITY_STATE`].includes(format);
					const includesCity = [`CITY_STATE`, `NAME_CITY_STATE`].includes(format);
					const includesState = [`CITY_STATE`, `NAME_CITY_STATE`].includes(format);
					let varName = includesName ? `venue_name` : `event_location`;
					let innerHTML = ``;

					if (includesName && this.isString(name, 1)) {
						innerHTML += name;
					}

					if (includesCity && this.isString(city, 1)) {
						if (innerHTML.length > 0) {
							innerHTML += `, `;
						}
						innerHTML += city;
					}

					if (includesState && this.isString(state, 1)) {
						if (innerHTML.length > 0) {
							innerHTML += `, `;
						}
						innerHTML += state;
					}

					return `${prefix}<var ${varName}>${innerHTML}</var>`;
				},
			},

			/**
			 * Simple mustache-style templating (lodash template).
			 *
			 * @method Templates#mustache
			 * @param {string} str - Template string with {{variable}} placeholders
			 * @returns {Function} Compiled template function
			 */
			mustache: {
				public: true,
				value(context, str) {
					var _ = this.require(`lodash`);
					return _.template(str);
				},
			},

			/**
			 * Preload compiled templates for a namespace.
			 *
			 * @method Templates#preload
			 * @param {Object} [options] - Preload options
			 * @param {string} [options.namespace] - Class namespace (defaults to caller's)
			 * @returns {Promise} Resolves when templates loaded
			 */
			preload: {
				async: true,
				public: true,
				value(context, options) {
					this.defaults(options, {
						namespace: context.namespace,
					});

					return this.require({
						deps: [options.namespace + `.templates`],
						fail: function (reason) {
							this.log({
								message: `Error loading templates: %O`,
								args: [reason],
							});
							return; // Swallow the error for now
						},
					});
				},
			},
		},
	});
});

//# sourceMappingURL=Templates.js.map